import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from '../utils/safeToast';
import {
  Monitor,
  ArrowUpDown,
  Filter,
  RefreshCw,
  AlertTriangle,
  Clock,
  CheckCircle,
  ChevronRight,
  Info
} from 'lucide-react';

const API = "/api";

const MonitoringPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    category: 'all',
    property_id: 'all',
    owner_id: 'all'
  });
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, propertiesRes, ownersRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/properties`),
        axios.get(`${API}/owners`)
      ]);
      setTasks(tasksRes.data);
      setProperties(propertiesRes.data);
      setOwners(ownersRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredAndSortedTasks = React.useMemo(() => {
    let result = [...tasks];
    
    // Apply filters
    if (filters.status !== 'all') {
      result = result.filter(t => t.status === filters.status);
    }
    if (filters.priority !== 'all') {
      result = result.filter(t => t.priority === filters.priority);
    }
    if (filters.category !== 'all') {
      result = result.filter(t => t.category === filters.category);
    }
    if (filters.property_id !== 'all') {
      result = result.filter(t => t.property_id === filters.property_id);
    }
    if (filters.owner_id !== 'all') {
      const ownerProps = properties.filter(p => p.owner_id === filters.owner_id).map(p => p.id);
      result = result.filter(t => ownerProps.includes(t.property_id));
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle special cases
      if (sortConfig.key === 'priority') {
        aVal = a.priority === 'hoch' ? 1 : 0;
        bVal = b.priority === 'hoch' ? 1 : 0;
      }
      if (sortConfig.key === 'status') {
        const order = { neu: 0, in_bearbeitung: 1, erledigt: 2 };
        aVal = order[a.status];
        bVal = order[b.status];
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [tasks, filters, sortConfig, properties]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'neu':
        return <div className="status-dot neu" />;
      case 'in_bearbeitung':
        return <div className="status-dot in-bearbeitung" />;
      case 'erledigt':
        return <div className="status-dot erledigt" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    const labels = { neu: 'Neu', in_bearbeitung: 'In Bearbeitung', erledigt: 'Erledigt' };
    return labels[status] || status;
  };

  const getCategoryLabel = (category) => {
    const labels = { eigentuemer: 'Eigentümer', reinigung: 'Reinigung', reparatur: 'Reparatur' };
    return labels[category] || category;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      priority: 'all',
      category: 'all',
      property_id: 'all',
      owner_id: 'all'
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== 'all');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E4C92]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="monitoring-page">
      {/* Header */}
      <div className="board-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Monitor className="w-8 h-8" />
          <div>
            <h1 className="text-2xl font-bold font-['Manrope']">Monitoring-Tafel</h1>
            <p className="text-blue-100 text-sm">{filteredAndSortedTasks.length} Aufgaben</p>
          </div>
        </div>
        <Button
          onClick={handleRefresh}
          variant="outline"
          className="bg-white/10 border-white/30 text-white hover:bg-white/20"
          disabled={refreshing}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-slate-500">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filter:</span>
            </div>
            
            <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
              <SelectTrigger className="w-[150px] bg-slate-50" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="neu">Neu</SelectItem>
                <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
                <SelectItem value="erledigt">Erledigt</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.priority} onValueChange={(v) => setFilters({...filters, priority: v})}>
              <SelectTrigger className="w-[140px] bg-slate-50" data-testid="filter-priority">
                <SelectValue placeholder="Priorität" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="hoch">Hoch</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.category} onValueChange={(v) => setFilters({...filters, category: v})}>
              <SelectTrigger className="w-[140px] bg-slate-50" data-testid="filter-category">
                <SelectValue placeholder="Kategorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="eigentuemer">Eigentümer</SelectItem>
                <SelectItem value="reinigung">Reinigung</SelectItem>
                <SelectItem value="reparatur">Reparatur</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.property_id} onValueChange={(v) => setFilters({...filters, property_id: v})}>
              <SelectTrigger className="w-[180px] bg-slate-50" data-testid="filter-property">
                <SelectValue placeholder="Objekt" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Objekte</SelectItem>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.owner_id} onValueChange={(v) => setFilters({...filters, owner_id: v})}>
              <SelectTrigger className="w-[180px] bg-slate-50" data-testid="filter-owner">
                <SelectValue placeholder="Eigentümer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Eigentümer</SelectItem>
                {owners.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-slate-500 text-sm">
                Filter zurücksetzen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Monitoring Table */}
      <Card className="bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full monitoring-table">
            <thead>
              <tr>
                <th className="text-left cursor-pointer" onClick={() => handleSort('priority')}>
                  <div className="flex items-center gap-1">
                    Prio <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="text-left cursor-pointer" onClick={() => handleSort('status')}>
                  <div className="flex items-center gap-1">
                    Status <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="text-left cursor-pointer" onClick={() => handleSort('title')}>
                  <div className="flex items-center gap-1">
                    Aufgabe <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="text-left">Objekt</th>
                <th className="text-left">Eigentümer</th>
                <th className="text-left">Kategorie</th>
                <th className="text-left cursor-pointer" onClick={() => handleSort('created_at')}>
                  <div className="flex items-center gap-1">
                    Erstellt <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="text-left">E-Mail</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedTasks.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-slate-500">
                    Keine Aufgaben gefunden
                  </td>
                </tr>
              ) : (
                filteredAndSortedTasks.map((task) => (
                  <tr
                    key={task.id}
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    className={task.priority === 'hoch' ? 'priority-high' : ''}
                    data-testid={`monitoring-row-${task.id}`}
                  >
                    <td>
                      {task.priority === 'hoch' ? (
                        <Badge className="bg-[#0E4C92] text-white border-0 font-bold">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          HOCH
                        </Badge>
                      ) : (
                        <span className="text-slate-500">Normal</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center">
                        {getStatusIcon(task.status)}
                        <span className={`font-medium ${
                          task.status === 'neu' ? 'text-blue-700' :
                          task.status === 'in_bearbeitung' ? 'text-amber-700' :
                          'text-emerald-700'
                        }`}>
                          {getStatusLabel(task.status)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="font-medium text-slate-900 max-w-xs truncate">
                        {task.title}
                      </div>
                    </td>
                    <td className="text-slate-600">{task.property_name || '-'}</td>
                    <td className="text-slate-600">{task.owner_name || '-'}</td>
                    <td>
                      <Badge variant="outline" className="text-xs">
                        {getCategoryLabel(task.category)}
                      </Badge>
                    </td>
                    <td className="text-slate-500 text-sm font-mono">
                      {formatDate(task.created_at)}
                    </td>
                    <td>
                      {task.email_status === 'versendet' ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Gesendet
                        </Badge>
                      ) : task.email_status === 'fehler' ? (
                        <Badge className="bg-red-100 text-red-700 border-0 text-xs">
                          Fehler
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 border-0 text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          Entwurf
                        </Badge>
                      )}
                    </td>
                    <td>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Statistics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{tasks.filter(t => t.status === 'neu').length}</div>
            <div className="text-sm text-slate-500">Neue Aufgaben</div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{tasks.filter(t => t.status === 'in_bearbeitung').length}</div>
            <div className="text-sm text-slate-500">In Bearbeitung</div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-[#0E4C92]">{tasks.filter(t => t.priority === 'hoch' && t.status !== 'erledigt').length}</div>
            <div className="text-sm text-slate-500">Hohe Priorität</div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-emerald-600">{tasks.filter(t => t.status === 'erledigt').length}</div>
            <div className="text-sm text-slate-500">Erledigt</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MonitoringPage;
